/**
 * PrintDialog - PDF print configuration dialog
 *
 * Provides a Material-UI dialog for configuring PDF print options:
 * - Page size selection (A4, A3, Letter, Custom)
 * - Portrait/landscape orientation
 * - DPI settings (72, 150, 300)
 * - Map title input
 * - Toggle legend, scale bar, north arrow
 *
 * **Validates: Requirements 36.1, 36.2, 36.3, 36.4, 36.5, 36.6**
 */

import React, { useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Box,
  Typography,
  Stack,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Print as PrintIcon,
  Landscape as LandscapeIcon,
  Portrait as PortraitIcon,
} from '@mui/icons-material';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import {
  closePrintDialog,
  setPageSize,
  setOrientation,
  setPrintDPI,
  setPrintTitle,
  toggleIncludeTitle,
  toggleIncludeLegend,
  toggleIncludeScaleBar,
  toggleIncludeNorthArrow,
  setPrintOptions,
  setExporting,
  setExportError,
} from '../store/slices/printExportSlice';
import type { PageSize, Orientation, DPI } from '../types/print';

interface PrintDialogProps {
  /** Callback to trigger PDF generation. Receives the current print options. */
  onPrint?: () => void;
}

const PrintDialog: React.FC<PrintDialogProps> = ({ onPrint }) => {
  const dispatch = useAppDispatch();
  const { printDialogOpen, printOptions, isExporting, exportError } = useAppSelector(
    (state) => state.printExport
  );

  const handleClose = useCallback(() => {
    dispatch(closePrintDialog());
  }, [dispatch]);

  const handlePageSizeChange = useCallback(
    (event: { target: { value: string } }) => {
      dispatch(setPageSize(event.target.value as PageSize));
    },
    [dispatch]
  );

  const handleOrientationChange = useCallback(
    (_: React.MouseEvent<HTMLElement>, newOrientation: string | null) => {
      if (newOrientation) {
        dispatch(setOrientation(newOrientation as Orientation));
      }
    },
    [dispatch]
  );

  const handleDPIChange = useCallback(
    (event: { target: { value: string } }) => {
      dispatch(setPrintDPI(Number(event.target.value) as DPI));
    },
    [dispatch]
  );

  const handleTitleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(setPrintTitle(event.target.value));
    },
    [dispatch]
  );

  const handleCustomWidthChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(setPrintOptions({ customWidth: Number(event.target.value) }));
    },
    [dispatch]
  );

  const handleCustomHeightChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(setPrintOptions({ customHeight: Number(event.target.value) }));
    },
    [dispatch]
  );

  const handlePrint = useCallback(() => {
    if (onPrint) {
      onPrint();
    }
  }, [onPrint]);

  return (
    <Dialog
      open={printDialogOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="print-dialog-title"
    >
      <DialogTitle id="print-dialog-title">
        <Box display="flex" alignItems="center" gap={1}>
          <PrintIcon />
          <Typography variant="h6">Print Map to PDF</Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {/* Page Size */}
          <FormControl fullWidth size="small">
            <InputLabel id="page-size-label">Page Size</InputLabel>
            <Select
              labelId="page-size-label"
              value={printOptions.pageSize}
              label="Page Size"
              onChange={handlePageSizeChange}
            >
              <MenuItem value="A4">A4 (210 × 297 mm)</MenuItem>
              <MenuItem value="A3">A3 (297 × 420 mm)</MenuItem>
              <MenuItem value="Letter">Letter (216 × 279 mm)</MenuItem>
              <MenuItem value="Custom">Custom</MenuItem>
            </Select>
          </FormControl>

          {/* Custom dimensions */}
          {printOptions.pageSize === 'Custom' && (
            <Box display="flex" gap={2}>
              <TextField
                label="Width (mm)"
                type="number"
                size="small"
                value={printOptions.customWidth ?? 210}
                onChange={handleCustomWidthChange}
                inputProps={{ min: 50, max: 1000 }}
                fullWidth
              />
              <TextField
                label="Height (mm)"
                type="number"
                size="small"
                value={printOptions.customHeight ?? 297}
                onChange={handleCustomHeightChange}
                inputProps={{ min: 50, max: 1000 }}
                fullWidth
              />
            </Box>
          )}

          {/* Orientation */}
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Orientation
            </Typography>
            <ToggleButtonGroup
              value={printOptions.orientation}
              exclusive
              onChange={handleOrientationChange}
              size="small"
              aria-label="page orientation"
            >
              <ToggleButton value="portrait" aria-label="portrait">
                <PortraitIcon sx={{ mr: 0.5 }} />
                Portrait
              </ToggleButton>
              <ToggleButton value="landscape" aria-label="landscape">
                <LandscapeIcon sx={{ mr: 0.5 }} />
                Landscape
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* DPI */}
          <FormControl fullWidth size="small">
            <InputLabel id="dpi-label">Resolution (DPI)</InputLabel>
            <Select
              labelId="dpi-label"
              value={printOptions.dpi}
              label="Resolution (DPI)"
              onChange={handleDPIChange}
            >
              <MenuItem value={72}>72 DPI (Screen)</MenuItem>
              <MenuItem value={150}>150 DPI (Draft)</MenuItem>
              <MenuItem value={300}>300 DPI (Print Quality)</MenuItem>
            </Select>
          </FormControl>

          {/* Title */}
          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={printOptions.includeTitle}
                  onChange={() => dispatch(toggleIncludeTitle())}
                />
              }
              label="Include Title"
            />
            {printOptions.includeTitle && (
              <TextField
                label="Map Title"
                size="small"
                fullWidth
                value={printOptions.title ?? ''}
                onChange={handleTitleChange}
                sx={{ mt: 1 }}
              />
            )}
          </Box>

          {/* Map elements */}
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Map Elements
            </Typography>
            <Stack>
              <FormControlLabel
                control={
                  <Switch
                    checked={printOptions.includeLegend}
                    onChange={() => dispatch(toggleIncludeLegend())}
                  />
                }
                label="Legend"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={printOptions.includeScaleBar}
                    onChange={() => dispatch(toggleIncludeScaleBar())}
                  />
                }
                label="Scale Bar"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={printOptions.includeNorthArrow}
                    onChange={() => dispatch(toggleIncludeNorthArrow())}
                  />
                }
                label="North Arrow"
              />
            </Stack>
          </Box>

          {/* Error display */}
          {exportError && (
            <Alert severity="error" onClose={() => dispatch(setExportError(null))}>
              {exportError}
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isExporting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handlePrint}
          disabled={isExporting}
          startIcon={isExporting ? <CircularProgress size={16} /> : <PrintIcon />}
        >
          {isExporting ? 'Generating...' : 'Print PDF'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PrintDialog;
